import { Link, useParams } from "react-router";
import { monthRange, shiftMonth } from "@/domain/accounting-period";
import { Decimal } from "@/domain/money";
import { computeMonth } from "@/finance/month";
import { formatDate, formatEurPlain, formatEurSigned, formatMonth, formatMonthShort, formatMonthTitle, formatPercent } from "@/lib/format";
import { INCOME_TYPE_LABELS } from "@/lib/labels";
import { BudgetMeter, ColumnChart, SERIES_COLORS } from "@/ui/charts";
import { EmptyState, Figure, Panel, PeriodSwitcher } from "@/ui/primitives";
import { monthParam, useFinanceIndex, useToday } from "../hooks";

export function MonthPage() {
  const { month: current } = useToday();
  const month = monthParam(useParams().ym, current);
  const index = useFinanceIndex();
  if (!index) return <p className="text-muted">Caricamento…</p>;

  const m = computeMonth(index, month);
  const trendMonths = monthRange(shiftMonth(month, -5), month);
  const trend = trendMonths.map((mm) => computeMonth(index, mm, { largestCount: 0 }));
  const variance = new Decimal(m.budget.variance);

  return (
    <div className="flex flex-col gap-5">
      <PeriodSwitcher
        label={formatMonthTitle(month)}
        previousHref={`/mese/${shiftMonth(month, -1)}`}
        nextHref={`/mese/${shiftMonth(month, 1)}`}
        previousLabel={`Mese precedente: ${formatMonth(shiftMonth(month, -1))}`}
        nextLabel={`Mese successivo: ${formatMonth(shiftMonth(month, 1))}`}
      >
        <p className="text-sm text-muted">Mese contabile</p>
      </PeriodSwitcher>

      <div className="grid grid-cols-2 gap-x-4 gap-y-5 rounded-xl border border-line bg-surface p-4 md:grid-cols-4 md:p-5">
        <Figure label="Entrate" amount={m.income.total} />
        <Figure label="Spese" amount={m.expenses.total} hint={m.expenses.average ? `media ${formatEurPlain(m.expenses.average)}` : undefined} />
        <Figure label="Risparmio" amount={m.savings.actual} signed hint={`tasso ${formatPercent(m.savings.rate)}`} />
        <Figure
          label="Budget spese"
          amount={m.budget.expenses}
          hint={m.budget.hasBudget ? (variance.isNegative() ? `superato di ${formatEurPlain(variance.neg())}` : `residuo ${formatEurPlain(variance)}`) : "non impostato"}
        />
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <Panel title="Saldo del mese">
          <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
            <dt className="text-muted">Saldo iniziale{m.balance.anchorLabel ? ` (${m.balance.anchorLabel})` : ""}</dt>
            <dd className="amount text-right">{formatEurPlain(m.balance.opening)}</dd>
            <dt className="text-muted">+ Entrate</dt>
            <dd className="amount text-right">{formatEurPlain(m.balance.income)}</dd>
            <dt className="text-muted">− Spese</dt>
            <dd className="amount text-right">{formatEurPlain(m.balance.expenses)}</dd>
            <dt className="border-t border-line pt-1.5 font-semibold">Saldo finale</dt>
            <dd className="amount border-t border-line pt-1.5 text-right font-semibold">{formatEurPlain(m.balance.closing)}</dd>
          </dl>
          {!m.balance.anchored ? <p className="mt-2 text-sm text-muted">Senza saldo di partenza il conteggio parte da zero.</p> : null}
        </Panel>

        <Panel title="Risparmio rispetto al previsto">
          <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
            <dt className="text-muted">Entrate previste</dt>
            <dd className="amount text-right">{formatEurPlain(m.budget.income)}</dd>
            <dt className="text-muted">Budget spese</dt>
            <dd className="amount text-right">{formatEurPlain(m.budget.expenses)}</dd>
            <dt className="text-muted">Risparmio previsto</dt>
            <dd className="amount text-right">{formatEurSigned(m.savings.projected)}</dd>
            <dt className="border-t border-line pt-1.5 font-semibold">Differenza sul previsto</dt>
            <dd className="amount border-t border-line pt-1.5 text-right font-semibold">{formatEurSigned(m.savings.vsProjected)}</dd>
          </dl>
          <Link to={`/budget/${month}`} className="mt-2 inline-block text-sm font-medium text-petrol">
            Modifica il budget
          </Link>
        </Panel>
      </div>

      <Panel title="Entrate" action={<Link to={`/entrate?mese=${month}`} className="text-sm font-medium text-petrol">Gestisci</Link>}>
        {m.income.entries.length === 0 ? (
          <p className="text-sm text-muted">Nessuna entrata registrata in questo mese.</p>
        ) : (
          <>
            <ul className="divide-y divide-line">
              {m.income.entries.map((i) => (
                <li key={i.id} className="flex justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="font-medium">{i.description}</span>
                    <span className="text-muted"> — {INCOME_TYPE_LABELS[i.type]}</span>
                  </span>
                  <span className="amount">{formatEurPlain(i.amount)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm text-muted">
              Stipendio {formatEurPlain(m.income.salary)}, altre entrate {formatEurPlain(m.income.extra)}
            </p>
          </>
        )}
      </Panel>

      <Panel title="Spese per gruppo" action={<Link to={`/spese?mese=${month}`} className="text-sm font-medium text-petrol">Elenco spese</Link>}>
        {m.expenses.byGroup.length === 0 ? (
          <EmptyState title={`Nessuna spesa in ${formatMonth(month)}`} />
        ) : (
          <ul className="space-y-2">
            {m.expenses.byGroup.map((g) => (
              <li key={g.groupId}>
                <details>
                  <summary className="cursor-pointer list-none">
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="font-medium">{g.label}</span>
                      <span className="amount">
                        {formatEurPlain(g.actual)}
                        {new Decimal(g.budget).gt(0) ? <span className="text-muted"> / {formatEurPlain(g.budget)}</span> : null}
                      </span>
                    </div>
                    <BudgetMeter actual={Number(g.actual)} budget={Number(g.budget)} />
                  </summary>
                  <table className="mt-2 w-full text-sm">
                    <thead className="text-left text-muted">
                      <tr>
                        <th className="py-1 font-normal">Categoria</th>
                        <th className="py-1 text-right font-normal">Speso</th>
                        <th className="py-1 text-right font-normal">Budget</th>
                        <th className="py-1 text-right font-normal">Differenza</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.categories.map((c) => (
                        <tr key={c.categoryId} className="border-t border-line">
                          <td className="py-1.5 pr-2">{c.label}</td>
                          <td className="amount py-1.5 text-right">{formatEurPlain(c.actual)}</td>
                          <td className="amount py-1.5 text-right text-muted">{formatEurPlain(c.budget)}</td>
                          <td className={`amount py-1.5 text-right ${new Decimal(c.variance).isNegative() ? "text-over" : ""}`}>{formatEurSigned(c.variance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <div className="grid gap-5 md:grid-cols-2">
        <Panel title="Spese fisse e discrezionali">
          <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
            <dt className="text-muted">Fisse</dt>
            <dd className="amount text-right">{formatEurPlain(m.expenses.byNature.fixed)}</dd>
            <dt className="text-muted">Discrezionali</dt>
            <dd className="amount text-right">{formatEurPlain(m.expenses.byNature.discretionary)}</dd>
            <dt className="text-muted">Non classificate</dt>
            <dd className="amount text-right">{formatEurPlain(m.expenses.byNature.unclassified)}</dd>
          </dl>
          {new Decimal(m.expenses.byNature.unclassified).gt(0) ? (
            <Link to="/impostazioni#classificazione" className="mt-2 inline-block text-sm font-medium text-petrol">
              Classifica le categorie
            </Link>
          ) : null}
        </Panel>

        <Panel title="Spese più alte">
          {m.expenses.largest.length === 0 ? (
            <p className="text-sm text-muted">Nessuna spesa.</p>
          ) : (
            <ul className="divide-y divide-line">
              {m.expenses.largest.map((e) => (
                <li key={e.id} className="flex justify-between gap-3 py-1.5 text-sm">
                  <span className="min-w-0 truncate">
                    {e.merchant ?? index.categories.get(e.categoryId)?.excelLabel}
                    <span className="text-muted"> — {formatDate(e.purchaseDate)}</span>
                  </span>
                  <span className="amount">{formatEurPlain(e.amountEur)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel title="Andamento degli ultimi sei mesi">
        <ColumnChart
          ariaLabel="Entrate e spese degli ultimi sei mesi"
          labels={trendMonths.map(formatMonthShort)}
          tooltipTitles={trendMonths.map(formatMonthTitle)}
          series={[
            { key: "income", label: "Entrate", color: SERIES_COLORS.income, values: trend.map((t) => Number(t.income.total)) },
            { key: "expense", label: "Spese", color: SERIES_COLORS.expense, values: trend.map((t) => Number(t.expenses.total)) },
          ]}
        />
      </Panel>
    </div>
  );
}
